import { Button, Content, Heading, Modal, Paragraph, Radio, RadioGroup, Span, Text } from "@hopper-ui/components";
import { getErrorMessage } from "@sg-protect/core";
import { submitTrialReasonMutationOptions, toSubmitTrialReasonBody } from "../api/routes/onboarding.ts";
import { ShareGateWordmarkLogo, useToast } from "@sg-protect/components";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { type ShellTranslationKey, useShellTranslation } from "../i18n/i18next.ts";
import { useSession } from "../SessionProvider.tsx";
import { MixpanelProperty } from "../tracking/mixpanelProperty.ts";
import { useTrackModalState } from "../tracking/useTrackEvent.ts";

const MODAL_NAME = "Trial Reason Modal";

interface TrialReasonOption {
    value: string;
    label: ShellTranslationKey;
    detail?: ShellTranslationKey;
}

const TRIAL_REASON_OPTIONS: TrialReasonOption[] = [
    { value: "onprem-to-365", label: "TrialReasonModal.reasons.onpremTo365" },
    {
        value: "tenant-to-tenant",
        label: "TrialReasonModal.reasons.tenantToTenant",
        detail: "TrialReasonModal.reasons.tenantToTenantDetail"
    },
    { value: "google-to-365", label: "TrialReasonModal.reasons.googleTo365" },
    { value: "mailbox", label: "TrialReasonModal.reasons.mailbox" },
    { value: "copilot", label: "TrialReasonModal.reasons.governance" },
    { value: "entra-id", label: "TrialReasonModal.reasons.entraId" }
];

interface TrialReasonModalProps {
    onSubmitted: () => void;
}

export function TrialReasonModal({ onSubmitted }: TrialReasonModalProps) {
    const { t } = useShellTranslation();
    const { firstName } = useSession();
    const [selectedReason, setSelectedReason] = useState<string | null>(null);
    const { mutate, isPending } = useMutation(submitTrialReasonMutationOptions());
    const toast = useToast();
    const [trackModalOpened, trackModalClosed] = useTrackModalState();
    const [hasTrackedOpen, setHasTrackedOpen] = useState(false);

    useEffect(() => {
        if (!hasTrackedOpen) {
            trackModalOpened(MODAL_NAME);
            setHasTrackedOpen(true);
        }
    }, [hasTrackedOpen, trackModalOpened]);

    const handleSubmit = () => {
        if (selectedReason === null) {
            return;
        }

        mutate(
            { body: toSubmitTrialReasonBody(selectedReason) },
            {
                onSuccess: () => {
                    trackModalClosed(MODAL_NAME, "Done", { [MixpanelProperty.Values]: selectedReason });
                    onSubmitted();
                },
                onError: error => {
                    toast.error(getErrorMessage(error, t("TrialReasonModal.submitError")));
                }
            }
        );
    };

    return (
        <Modal isOpen isDismissable={false} isKeyboardDismissDisabled aria-label={t("TrialReasonModal.title")}>
            <Heading>
                <ShareGateWordmarkLogo />
            </Heading>

            <Content marginTop="stack-md">
                <Paragraph marginBottom="core_160" color="neutral-hover">
                    {t("TrialReasonModal.greeting", { firstName })}
                </Paragraph>
                <Paragraph marginBottom="core_160" data-public>
                    {t("TrialReasonModal.question")}
                </Paragraph>

                <RadioGroup
                    value={selectedReason}
                    onChange={setSelectedReason}
                    gap="core_80"
                    marginBottom="core_320"
                    aria-label={t("TrialReasonModal.title")}
                >
                    {TRIAL_REASON_OPTIONS.map(({ value, label, detail }) => (
                        <Radio value={value} key={value}>
                            <Text size="sm">
                                <Span data-public>{t(label)}</Span>
                                {detail && (
                                    <Span data-public color="neutral-weakest">
                                        {" "}
                                        {t(detail)}
                                    </Span>
                                )}
                            </Text>
                        </Radio>
                    ))}
                </RadioGroup>

                <Button
                    onPress={handleSubmit}
                    variant="primary"
                    isFluid
                    isDisabled={selectedReason === null || isPending}
                    isLoading={isPending}
                    data-public
                >
                    {t("TrialReasonModal.done")}
                </Button>
            </Content>
        </Modal>
    );
}
